import { Input, ChangeDetectorRef, Directive, Output, EventEmitter, AfterViewChecked } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, AbstractControl, Validators } from '@angular/forms';

/**
 * @directive
 * @description Base abstract class for payment modal components.
 * Provides common functionality for managing payment lines within a modal dialog.
 * Implements AfterViewChecked to handle responsive behavior like maximizing the dialog on small screens.
 */
@Directive()
export abstract class BasePaymentModalComponent implements AfterViewChecked {
	/**
	 * @Input()
	 * @description The main FormArray representing the payment lines, passed from the parent component.
	 */
	@Input() lines!: FormArray;
	/**
	 * @Input()
	 * @description Flag indicating if the form has been submitted, used for validation display.
	 * Controlled primarily by the parent component.
	 */
	@Input() submitted: boolean = false;
	/**
	 * @Input()
	 * @description The current language setting, defaults to 'en'.
	 */
	@Input() curentLang: string = 'en';
	/**
	 * @Input()
	 * @description Flag to enable debug logging.
	 */
	@Input() debug: boolean = false;
	/**
	 * @Output()
	 * @description Emits the updated lines array when the user saves changes in the modal.
	 */
	@Output() saveLines = new EventEmitter<any[]>();

	/**
	 * @description The total amount calculated from the lines, formatted for display.
	 */
	displayTotalAmount: number = 0;

	/**
	 * @description Controls the visibility of the modal dialog.
	 */
	showDialog: boolean = false;
	/**
	 * @description Determines the mode of the dialog ('add' or 'edit').
	 */
	dialogMode: 'add' | 'edit' = 'add';
	/**
	 * @description The index of the line being edited, or null if in 'add' mode or no line is selected.
	 */
	editIndex: number | null = null;
	/**
	 * @description Temporary storage for line data, used primarily during editing.
	 */
	tempLine: any = null;

	/**
	 * @description A local copy of the lines FormArray used within the modal to avoid modifying the original input directly until save.
	 */
	localLines!: FormArray;

	/**
	 * @private
	 * @description Tracks if the dialog has been maximized once on small screens to prevent repeated maximization.
	 */
	private maximizedOnce = false;

	/**
	 * @constructor
	 * @param {FormBuilder} fb - The Angular FormBuilder service.
	 * @param {ChangeDetectorRef} cdr - The Angular ChangeDetectorRef service.
	 */
	constructor(protected fb: FormBuilder, protected cdr: ChangeDetectorRef) {}

	/**
	 * @description Removes a line from the local lines FormArray at the specified index.
	 * @param {number} index - The index of the line to remove.
	 */
	removeLine(index: number): void {
		if (this.localLines && this.localLines.length > 0) {
			this.localLines.removeAt(index);
			this.updateDisplayTotalLocal();
			this.cdr.markForCheck();
		}
	}

	/**
	 * @description Calculates the total amount from the main `lines` FormArray.
	 * Can optionally override a specific line's amount for calculation previews.
	 * @param {number} [overrideIndex] - The index of the line whose amount should be overridden.
	 * @param {number | null} [overrideValue] - The value to use for the overridden line's amount.
	 * @returns {number} The calculated total amount.
	 */
	calculateTotalAmount(overrideIndex?: number, overrideValue?: number | null): number {
		if (!this.lines || !this.lines.controls) return 0;
		return this.lines.controls.reduce((sum, group, currentIndex) => {
			let lineValue: number | null | undefined;
			if (currentIndex === overrideIndex) {
				lineValue = overrideValue;
			} else {
				lineValue = group.get('amount')?.value;
			}
			const num = typeof lineValue === 'number' ? lineValue : parseFloat(lineValue as any);
			return sum + (isNaN(num) ? 0 : num);
		}, 0);
	}

	/**
	 * @description Updates the `displayTotalAmount` property based on the main `lines` FormArray.
	 * Uses `calculateTotalAmount` for the calculation.
	 * @param {number} [overrideIndex] - Optional index to override for calculation.
	 * @param {number | null} [overrideValue] - Optional value to override for calculation.
	 */
	updateDisplayTotal(overrideIndex?: number, overrideValue?: number | null): void {
		const newTotal = this.calculateTotalAmount(overrideIndex, overrideValue);
		if (!isNaN(newTotal) && this.displayTotalAmount !== newTotal) {
			this.displayTotalAmount = newTotal;
			this.cdr.markForCheck();
		}
	}

	/**
	 * @description Initializes the `localLines` FormArray by creating a deep copy of the input `lines`,
	 * including controls and their validators.
	 * Sets up value change subscription to automatically update the local total.
	 */
	initLocalLines(): void {
		const controls = this.lines?.controls || [];
		this.localLines = this.fb.array(
			controls.map(ctrl => {
				const group = ctrl as FormGroup;
				const newGroup = this.fb.group({});
				Object.keys(group.controls).forEach(key => {
					const control = group.controls[key];
					newGroup.addControl(key, this.fb.control(control.value, control.validator));
				});
				return newGroup;
			})
		);

		this.updateDisplayTotalLocal();

		this.localLines.valueChanges.subscribe(() => {
			this.updateDisplayTotalLocal();
		});
	}

	/**
	 * @description Getter to check if the `localLines` FormArray is invalid.
	 * @returns {boolean} True if `localLines` is invalid or not initialized, false otherwise.
	 */
	get localLinesInvalid(): boolean {
		return this.localLines?.invalid ?? true;
	}

	/**
	 * @description Calculates and updates the `displayTotalAmount` based on the `localLines` FormArray.
	 * Rounds the result to 2 decimal places.
	 */
	updateDisplayTotalLocal(): void {
		const newTotal = this.localLines.controls.reduce((sum, group) => {
			const val = group.get('amount')?.value;
			const num = typeof val === 'number' ? val : parseFloat(val as any);
			return sum + (isNaN(num) ? 0 : num);
		}, 0);
		// Round to 2 decimal places to avoid floating point issues
		this.displayTotalAmount = Math.round(newTotal * 100) / 100;
		this.cdr.markForCheck();
	}

	/**
	 * @protected
	 * @abstract
	 * @description Abstract method that must be implemented by subclasses to create an empty FormGroup
	 * representing a single line item specific to the payment type.
	 * @returns {FormGroup} An empty FormGroup for a new line.
	 */
	protected abstract createEmptyLineGroup(): FormGroup;

	/**
	 * @description Opens the modal dialog. Initializes local lines, sets the mode, and handles initial state.
	 * In 'add' mode, it automatically adds an empty line.
	 * @param {'add' | 'edit'} mode - The mode to open the dialog in ('add' or 'edit').
	 * @param {any} [lineData] - The data of the line to edit (used in 'edit' mode).
	 * @param {number} [index] - The index of the line to edit (used in 'edit' mode).
	 */
	openDialog(mode: 'add' | 'edit', lineData?: any, index?: number) {
this.initLocalLines();
		this.dialogMode = mode;
		this.editIndex = index ?? null;
		this.tempLine = lineData ? { ...lineData } : null;
		this.showDialog = true;
		this.submitted = false;
		this.maximizedOnce = false;

		if (mode === 'add') {
			this.localLines.push(this.createEmptyLineGroup());
			this.updateDisplayTotalLocal();
		}
		this.cdr.markForCheck();
	}

	/**
	 * @description Angular lifecycle hook called after every check of the component's view.
	 * Used here to maximize the dialog automatically on small screens when it first opens.
	 */
	ngAfterViewChecked(): void {
		if (
			this.showDialog &&
			window.innerWidth < 768 &&
			!this.maximizedOnce
		) {
			this.clickMaximizeButton();
			this.maximizedOnce = true;
		}
	}

	/**
	 * @protected
	 * @description Programmatically clicks the maximize button of the PrimeNG dialog.
	 * Used for responsive behavior on small screens.
	 */
	protected clickMaximizeButton() {
		// Note: This relies on PrimeNG's v13 internal CSS classes and might break with updates.
		const dialogEl = document.querySelector('.p-dialog') as HTMLElement;
		if (dialogEl) {
			const maximizeBtn = dialogEl.querySelector('.p-dialog-header-maximize') as HTMLElement;
			if (maximizeBtn) {
				maximizeBtn.click();
			}
		}
	}

	/**
	 * @description Closes the modal dialog and resets its state.
	 * Re-initializes local lines to discard any unsaved changes.
	 */
	closeDialog() {
this.initLocalLines();
		this.showDialog = false;
		this.tempLine = null;
		this.editIndex = null;
		this.cdr.markForCheck();
	}

	/**
	 * @description Handles input changes for the amount field within the local lines FormArray.
	 * Updates the control's value, triggers validation, and updates the local total display.
	 * @param {object} event - The event object, typically from an input component like p-inputNumber.
	 * @param {Event} event.originalEvent - The original DOM event.
	 * @param {number | null} event.value - The new value from the input.
	 * @param {number} index - The index of the line whose amount changed.
	 */
	onAmountInputLocal(event: { originalEvent: Event, value: number | null }, index: number): void {
		const newValue = event.value;
		const lineControl = this.localLines.at(index);
		const amountControl = lineControl?.get('amount');
		if (amountControl) {
			amountControl.patchValue(newValue, { emitEvent: false });
			this.localLines.updateValueAndValidity({ emitEvent: true });
			this.updateDisplayTotalLocal();
			this.cdr.markForCheck();
		}
	}

	/**
	 * @description Handles the save action. Marks all controls as touched, sets submitted flag,
	 * checks validity, emits the `saveLines` event with the local lines data if valid, and closes the dialog.
	 */
	onSave() {
		this.submitted = true;
		// Mark all as touched BEFORE checking validity to ensure errors are shown
		this.localLines.markAllAsTouched();
		this.cdr.markForCheck();

		if (this.localLines.invalid) {
return;
		}

this.saveLines.emit(this.localLines.getRawValue());
		this.closeDialog();
	}

	/**
	 * @description Adds a new line using the provided factory function, but only if the last existing line is valid.
	 * If the last line is invalid, it marks it as touched to display errors.
	 * @param {() => FormGroup} createGroup - A function that returns a new FormGroup for the line.
	 * @param {boolean} [updateTotal=true] - Whether to update the display total after adding the line.
	 */
	addLineWithValidation(createGroup: () => FormGroup, updateTotal: boolean = true): void {
		if (this.localLines.length > 0) {
			const lastLine = this.localLines.at(this.localLines.length - 1);
			if (lastLine.invalid) {
				lastLine.markAllAsTouched();
				this.submitted = true;
				this.cdr.markForCheck();
				return;
			}
		}
		this.localLines.push(createGroup());
		if (updateTotal) this.updateDisplayTotalLocal();
		this.cdr.markForCheck();
	}

	/**
	 * @description Validates the last line in the `localLines` array. If invalid, marks it as touched.
	 * Useful before performing an action like saving that requires all lines to be valid.
	 * @returns {boolean} True if the last line is valid (or if there are no lines), false otherwise.
	 */
	validateLastLineOrMarkAll(): boolean {
		if (this.localLines.length > 0) {
			const lastLine = this.localLines.at(this.localLines.length - 1);
			if (lastLine.invalid) {
				lastLine.markAllAsTouched();
				this.submitted = true;
				this.cdr.markForCheck();
				return false;
			}
		}
		return true;
	}

	/**
	 * @description Getter to check if the `localLines` array is empty.
	 * @returns {boolean} True if there are no local lines, false otherwise.
	 */
	get noLines(): boolean {
		return this.localLines?.length === 0;
	}
}
